import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { downloadPolicy } from './MyPolicies';

// Policy Management — Stage 1: register, policy detail, section editor and
// applicability. Published versions are never edited; changes create a version.
const ST_COLOR = { draft:'var(--text2)', under_review:'var(--accent)', approved:'var(--green)',
  published:'var(--green)', superseded:'var(--text2)', archived:'var(--text2)' };
const Pill = ({ s }) => (
  <span style={{ fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:20,
    background:'var(--bg3)', color:ST_COLOR[s] || 'var(--text2)' }}>{String(s || '').replace('_',' ')}</span>
);
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

export default function Policies() {
  const { user } = useAuth();
  const canManage = ['admin','it_head'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    api.get('/policies', { params }).then(r => setRows(r.data)).catch(()=>{});
  }, [q, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/policies/categories').then(r => setCats(r.data)).catch(()=>{}); }, []);

  if (openId) return <PolicyDetail id={openId} canManage={canManage} cats={cats}
                       onClose={() => { setOpenId(null); load(); }} />;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>📋 Policy Management</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>
            Policies are written and versioned here. A published version is never edited — any change creates the next version.
          </p>
        </div>
        {canManage && <button className="btn btn-primary" onClick={() => setNewOpen(true)}>+ New Policy</button>}
      </div>

      <div className="flex-gap" style={{ gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <input placeholder="Search title or policy ID…" value={q} onChange={e => setQ(e.target.value)} style={{ padding:'6px 8px', minWidth:200 }} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All statuses</option>
          {['draft','under_review','approved','published','superseded','archived'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
      </div>

      {!rows.length ? (
        <div className="card"><div className="empty-state">
          <h3>No policies yet</h3>
          <p>{canManage ? 'Create your first policy to start building the register.' : 'Nothing published for you yet.'}</p>
        </div></div>
      ) : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Policy ID</th><th>Title</th><th>Category</th><th>Owner</th><th>Version</th><th>Status</th><th>Review due</th></tr></thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id} style={{ cursor:'pointer' }} onClick={() => setOpenId(p.id)}>
                  <td className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{p.policy_code}</td>
                  <td style={{ fontWeight:500 }}>{p.title}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{p.category_name || '—'}</td>
                  <td style={{ fontSize:12 }}>{p.owner_name || '—'}</td>
                  <td style={{ fontSize:12 }}>{p.current_version || <span style={{ color:'var(--text2)' }}>draft</span>}</td>
                  <td><Pill s={p.status} /></td>
                  <td style={{ fontSize:12, color: p.review_overdue ? 'var(--red)' : 'var(--text2)' }}>
                    {p.review_date ? fmt(p.review_date) : '—'}{p.review_overdue ? ' · overdue' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {newOpen && <NewPolicy cats={cats} onClose={() => setNewOpen(false)}
        onSaved={id => { setNewOpen(false); load(); setOpenId(id); }} />}
    </div>
  );
}

function NewPolicy({ cats, onClose, onSaved }) {
  const [f, setF] = useState({ title:'', category_id:'', description:'', effective_date:'', review_date:'', review_frequency_months:12 });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const save = async () => {
    if (!f.title.trim())  { setErr('Title is required'); return; }
    if (!f.category_id)   { setErr('Pick a category'); return; }
    setBusy(true); setErr('');
    try { const r = await api.post('/policies', f); onSaved(r.data.id); }
    catch (e) { setErr(e.response?.data?.error || 'Could not create the policy'); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header"><span className="modal-title">New Policy</span>
          <button className="modal-close" onClick={onClose}>×</button></div>
        <div className="form-group"><label>Title *</label>
          <input value={f.title} onChange={e => setF(s => ({ ...s, title:e.target.value }))} placeholder="e.g. IT Acceptable Use Policy" /></div>
        <div className="form-row">
          <div className="form-group"><label>Category *</label>
            <select value={f.category_id} onChange={e => setF(s => ({ ...s, category_id:e.target.value }))}>
              <option value="">— select —</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ fontSize:11, color:'var(--text2)', marginTop:3 }}>The policy ID is generated from this, e.g. IT-POL-001.</div></div>
          <div className="form-group"><label>Effective date</label>
            <input type="date" value={f.effective_date} onChange={e => setF(s => ({ ...s, effective_date:e.target.value }))} /></div>
          <div className="form-group"><label>Review date</label>
            <input type="date" value={f.review_date} onChange={e => setF(s => ({ ...s, review_date:e.target.value }))} /></div>
        </div>
        <div className="form-group"><label>Description</label>
          <textarea rows={2} value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))} /></div>
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Creating…' : 'Create draft'}</button>
        </div>
      </div>
    </div>
  );
}

function PolicyDetail({ id, canManage, cats, onClose }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('content');
  const [pub, setPub] = useState(null);      // version being published
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get(`/policies/${id}`).then(r => setD(r.data)).catch(()=>{}), [id]);
  useEffect(() => { load(); }, [load]);
  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 3000); };
  if (!d) return null;

  const draft = d.versions.find(v => ['draft','rejected'].includes(v.status));
  const published = d.versions.find(v => v.status === 'published');
  const review = d.versions.find(v => v.status === 'under_review');
  const approved = d.versions.find(v => v.status === 'approved');

  const newVersion = async () => {
    try { const r = await api.post(`/policies/${id}/versions`); flash(`✓ Draft ${r.data.version_no} created`); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not start a new version'); }
  };

  const TABS = [['content','Content'],['applicability','Applicability'],['compliance','Compliance'],['versions','Versions'],['activity','Activity']];

  const submit = async () => {
    try { await api.post(`/policies/versions/${draft.id}/submit`); flash('✓ Submitted for review'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not submit'); }
  };
  const decide = async decision => {
    const comments = decision === 'rejected' ? window.prompt('Reason for rejection (required):') : window.prompt('Comments (optional):') || '';
    if (decision === 'rejected' && !comments) return;
    try { await api.post(`/policies/versions/${review.id}/decision`, { decision, comments }); flash(`✓ ${decision}`); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not record the decision'); }
  };

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ marginBottom:12 }}>◀ Policy register</button>

      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
        <div>
          <div className="flex-gap" style={{ gap:10, alignItems:'center' }}>
            <span style={{ fontSize:22, fontWeight:700 }}>{d.title}</span>
            <Pill s={d.status} />
          </div>
          <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:4 }}>
            <b className="mono" style={{ color:'var(--accent)' }}>{d.policy_code}</b>
            <span style={{ margin:'0 8px' }}>·</span>{d.category_name}
            <span style={{ margin:'0 8px' }}>·</span>Owner: {d.owner_name || '—'}
            {published && <><span style={{ margin:'0 8px' }}>·</span>Published v{published.version_no}</>}
            {d.review_date && <><span style={{ margin:'0 8px' }}>·</span>Review {fmt(d.review_date)}</>}
          </div>
        </div>
        {canManage && (
          <div className="flex-gap" style={{ gap:8, flexWrap:'wrap' }}>
            {draft && <button className="btn btn-secondary" onClick={submit}>Submit for review</button>}
            {review && <>
              <button className="btn btn-secondary" onClick={() => decide('rejected')}>Reject</button>
              <button className="btn btn-primary" onClick={() => decide('approved')}>Approve</button>
            </>}
            {(approved || draft) && <button className="btn btn-primary" onClick={() => setPub(approved || draft)}>Publish…</button>}
            {!draft && !review && !approved && <button className="btn btn-primary" onClick={newVersion}>+ New version</button>}
          </div>
        )}
      </div>

      <div className="flex-gap" style={{ gap:0, borderBottom:'1px solid var(--border)', margin:'14px 0 16px', flexWrap:'wrap' }}>
        {TABS.map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding:'9px 18px', fontSize:13, background:'transparent',
            border:'none', cursor:'pointer', marginBottom:-1,
            borderBottom: tab===t ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab===t ? 'var(--accent)' : 'var(--text2)', fontWeight: tab===t ? 600 : 400 }}>{l}</button>
        ))}
      </div>

      {msg && <div style={{ fontSize:13, marginBottom:10, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}

      {/* The editor stays MOUNTED and is only hidden when another tab is shown.
          Unmounting it threw away everything typed since the last save, and you
          have to visit Applicability before you can publish — so the normal way
          of writing a policy silently lost most of it. */}
      <div style={{ display: tab === 'content' ? 'block' : 'none' }}>
        <SectionEditor policy={d} draft={draft} published={published}
          canManage={canManage} onSaved={() => { flash('✓ Saved'); load(); }} flash={flash} />
      </div>
      {tab === 'applicability' && <Applicability policyId={id} rules={d.applicability} canManage={canManage}
        onSaved={() => { flash('✓ Applicability updated'); load(); }} />}
      {tab === 'versions' && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Version</th><th>Status</th><th>Change</th><th>Created by</th><th>Approved by</th><th>Effective</th><th>Download</th></tr></thead>
            <tbody>{d.versions.map(v => (
              <tr key={v.id}>
                <td style={{ fontWeight:600 }}>{v.version_no}</td>
                <td><Pill s={v.status} /></td>
                <td style={{ fontSize:12 }}>{v.change_type || '—'}{v.change_summary ? ` · ${v.change_summary}` : ''}</td>
                <td style={{ fontSize:12 }}>{v.created_by_name || '—'}</td>
                <td style={{ fontSize:12 }}>{v.approved_by_name || '—'}</td>
                <td style={{ fontSize:12 }}>{v.effective_date ? fmt(v.effective_date) : '—'}</td>
                <td>
                  <div className="flex-gap" style={{ gap:6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => downloadPolicy(v.id, 'pdf', flash)}>PDF</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => downloadPolicy(v.id, 'docx', flash)}>Word</button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
      {tab === 'compliance' && <Compliance id={id} />}
      {tab === 'activity' && <Activity id={id} />}

      {pub && <PublishModal version={pub} policyId={id} onClose={() => setPub(null)}
        onDone={r => { setPub(null); flash(`✓ Published v${r.version_no} — ${r.audience} staff must acknowledge`); load(); }} />}
    </div>
  );
}

// Classify the change at publish time — this decides what staff are asked to do.
function PublishModal({ version, policyId, onClose, onDone }) {
  const [f, setF] = useState({ change_type:'minor', change_summary:'', effective_date:'', ack_due_date:'' });
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  useEffect(() => { api.get(`/policies/${policyId}/audience`).then(r => setAud(r.data)).catch(()=>{}); }, [policyId]);
  const go = async () => {
    if (!f.change_summary.trim()) { setErr('A short change summary is required — staff see this first.'); return; }
    setBusy(true); setErr('');
    try { const r = await api.post(`/policies/versions/${version.id}/publish`, f); onDone(r.data); }
    catch (e) { setErr(e.response?.data?.error || 'Publish failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:580 }}>
        <div className="modal-header"><span className="modal-title">Publish v{version.version_no}</span>
          <button className="modal-close" onClick={onClose}>×</button></div>

        <div className="form-group"><label>How big is this change? *</label>
          <div style={{ display:'grid', gap:8 }}>
            {[['minor','Minor change','Wording, typo, clarification, contact detail. Staff read only what changed.'],
              ['major','Major revision','New requirement, security or responsibility change. Staff must read the whole policy.']].map(([v,t,desc]) => (
              <label key={v} className="card opt opt-top" style={{ padding:'10px 12px', margin:0,
                borderLeft:`3px solid ${v==='major'?'var(--red)':'#FF9900'}`, background: f.change_type===v ? 'var(--bg3)' : undefined }}>
                <input type="radio" name="ct" checked={f.change_type===v} onChange={() => setF(s => ({ ...s, change_type:v }))} />
                <span><b style={{ fontSize:13 }}>{t}</b><div style={{ fontSize:12, color:'var(--text2)' }}>{desc}</div></span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group"><label>Change summary *</label>
          <textarea rows={2} value={f.change_summary} onChange={e => setF(s => ({ ...s, change_summary:e.target.value }))}
            placeholder="e.g. Password change interval reduced from 90 to 60 days" /></div>
        <div className="form-row">
          <div className="form-group"><label>Effective date</label>
            <input type="date" value={f.effective_date} onChange={e => setF(s => ({ ...s, effective_date:e.target.value }))} /></div>
          <div className="form-group"><label>Acknowledge by</label>
            <input type="date" value={f.ack_due_date} onChange={e => setF(s => ({ ...s, ack_due_date:e.target.value }))} /></div>
        </div>

        <div style={{ fontSize:13, padding:'8px 10px', borderRadius:6, background:'var(--bg3)' }}>
          {aud ? <>This will ask <b>{aud.count}</b> staff member(s) to acknowledge{aud.has_rules ? '' : ' (no applicability rules set — that means everyone)'}.</> : 'Working out the audience…'}
        </div>
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={go}>{busy ? 'Publishing…' : 'Publish'}</button>
        </div>
      </div>
    </div>
  );
}

function Compliance({ id }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/policies/${id}/compliance`).then(r => setC(r.data)).catch(()=>{}); }, [id]);
  if (!c) return null;
  if (c.empty) return <div className="card"><div className="empty-state"><p>Publish a version to start tracking acknowledgements.</p></div></div>;
  const Stat = ({ l, v, col }) => (
    <div className="card" style={{ padding:'12px 14px', textAlign:'center' }}>
      <div style={{ fontSize:12, color:'var(--text2)' }}>{l}</div>
      <div style={{ fontSize:24, fontWeight:700, color:col || 'var(--text)' }}>{v}</div>
    </div>
  );
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:12, marginBottom:14 }}>
        <Stat l="Applicable staff" v={c.total} />
        <Stat l="Acknowledged" v={c.acknowledged} col="var(--green)" />
        <Stat l="Viewed, not acked" v={c.viewed} col="#B26A00" />
        <Stat l="Pending" v={c.pending} col={c.pending ? 'var(--red)' : 'var(--text)'} />
        <Stat l="Overdue" v={c.overdue} col={c.overdue ? 'var(--red)' : 'var(--text)'} />
        <Stat l="Compliance" v={c.percent + '%'} col={c.percent === 100 ? 'var(--green)' : 'var(--text)'} />
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', padding:'10px 12px' }}>WHO HAS ACKNOWLEDGED</div>
        <div className="table-wrap"><table>
          <thead><tr><th>Staff</th><th>Campus</th><th>Status</th><th>Viewed</th><th>Acknowledged</th></tr></thead>
          <tbody>{c.people.map(p => (
            <tr key={p.id}>
              <td style={{ fontWeight:500 }}>{p.full_name}</td>
              <td style={{ fontSize:12, color:'var(--text2)' }}>{p.campus_name || '—'}</td>
              <td><span className="badge" style={{ fontSize:10.5,
                background: p.status==='acknowledged' ? 'rgba(45,168,102,0.15)' : 'var(--bg3)',
                color: p.status==='acknowledged' ? 'var(--green)' : 'var(--text2)' }}>{p.status}</span></td>
              <td style={{ fontSize:12, color:'var(--text2)' }}>{p.viewed_at ? new Date(p.viewed_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}</td>
              <td style={{ fontSize:12 }}>{p.acknowledged_at ? new Date(p.acknowledged_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}

// The web editor. Only a draft is editable; a published version is read-only.
function SectionEditor({ policy, draft, published, canManage, onSaved, flash }) {
  const target = draft || published;
  const [sections, setSections] = useState([]);
  const [saved, setSaved] = useState('');      // the last saved content, to spot edits
  const [busy, setBusy] = useState(false);
  const editable = canManage && !!draft;
  const dirty = editable && JSON.stringify(sections) !== saved;

  useEffect(() => {
    if (!target) { setSections([]); setSaved(''); return; }
    api.get(`/policies/versions/${target.id}`).then(r => {
      const list = r.data.sections.length ? r.data.sections
        : [{ heading:'Purpose', body_html:'' }, { heading:'Scope', body_html:'' }, { heading:'Responsibilities', body_html:'' }];
      setSections(list); setSaved(JSON.stringify(list));
    }).catch(()=>{});
  }, [target && target.id]); // eslint-disable-line

  // Closing the tab or hitting reload with unsaved sections asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/policies/versions/${draft.id}/sections`, { sections });
      setSaved(JSON.stringify(sections));
      onSaved();
    }
    catch (e) { flash(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };
  const setS = (i, patch) => setSections(list => list.map((s, j) => j === i ? { ...s, ...patch } : s));
  const move = (i, dir) => setSections(list => {
    const n = [...list]; const j = i + dir;
    if (j < 0 || j >= n.length) return n;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  if (!target) return <div className="card"><div className="empty-state"><p>No version yet.</p></div></div>;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
        <div style={{ fontSize:12.5, color:'var(--text2)' }}>
          {draft
            ? <>Editing draft <b style={{ color:'var(--text)' }}>v{draft.version_no}</b> — {sections.length} section(s)
                {dirty && <b style={{ color:'#B26A00', marginLeft:8 }}>● unsaved changes</b>}</>
            : <>Showing published <b style={{ color:'var(--text)' }}>v{published.version_no}</b> (read-only — use “New version” to change it)</>}
        </div>
        {editable && (
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setSections(l => [...l, { heading:'', body_html:'' }])}>+ Add section</button>
            <button className={`btn btn-sm ${dirty ? 'btn-primary' : 'btn-secondary'}`} disabled={busy} onClick={save}>
              {busy ? 'Saving…' : dirty ? 'Save draft *' : 'Save draft'}
            </button>
          </div>
        )}
      </div>

      {!sections.length ? <div className="card"><div className="empty-state"><p>No content yet.</p></div></div> :
        sections.map((s, i) => (
          <div key={i} className="card" style={{ marginBottom:10 }}>
            <div className="flex-gap" style={{ gap:8, alignItems:'center', marginBottom:6 }}>
              <span style={{ fontSize:11, color:'var(--text2)', fontWeight:600, minWidth:18 }}>{i + 1}.</span>
              {editable
                ? <input value={s.heading || ''} onChange={e => setS(i, { heading:e.target.value })}
                    placeholder="Section heading e.g. Password Management"
                    style={{ flex:1, fontWeight:600, padding:'5px 8px' }} />
                : <b style={{ flex:1 }}>{s.heading}</b>}
              {editable && (
                <div className="flex-gap" style={{ gap:4 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => move(i, -1)} title="Move up">↑</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => move(i, 1)} title="Move down">↓</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setSections(l => l.filter((_, j) => j !== i))}>×</button>
                </div>
              )}
            </div>
            {editable
              ? <textarea rows={5} value={s.body_html || ''} onChange={e => setS(i, { body_html:e.target.value })}
                  placeholder="Write the section content…" style={{ width:'100%' }} />
              : <div style={{ fontSize:13.5, whiteSpace:'pre-wrap', color:'var(--text2)' }}>{s.body_html || '—'}</div>}
          </div>
        ))}
    </div>
  );
}

function Applicability({ policyId, rules, canManage, onSaved }) {
  const [campuses, setCampuses] = useState([]);
  const [depts, setDepts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sel, setSel] = useState({ campus:[], department:[], role:[] });
  const [audience, setAudience] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active))).catch(()=>{});
    api.get('/master/departments').then(r => setDepts(r.data)).catch(()=>{});
    api.get('/master/roles').then(r => setRoles(r.data.filter(x => x.active))).catch(()=>{});
  }, []);
  useEffect(() => {
    setSel({
      campus: rules.filter(r => r.rule_type === 'campus').map(r => r.ref_id),
      department: rules.filter(r => r.rule_type === 'department').map(r => r.ref_id),
      role: rules.filter(r => r.rule_type === 'role').map(r => r.ref_id),
    });
  }, [rules]);
  const [drift, setDrift] = useState(null);
  const loadAudience = useCallback(() => {
    api.get(`/policies/${policyId}/audience`).then(r => setAudience(r.data)).catch(()=>{});
    // Publishing fixes the audience at that moment, so the people who already
    // hold the policy can differ from who it now applies to.
    if (canManage) api.get(`/policies/${policyId}/audience-drift`).then(r => setDrift(r.data)).catch(()=>{});
  }, [policyId, canManage]);
  useEffect(() => { loadAudience(); }, [loadAudience]);

  const reapply = async () => {
    const rm = drift.remove.length, add = drift.add.length;
    const ok = window.confirm(
      `Apply this applicability to the published version?\n\n` +
      (rm ? `• ${rm} person(s) will lose it — it disappears from their My Policies and their notification is removed.\n` : '') +
      (add ? `• ${add} person(s) will be asked to acknowledge it.\n` : '') +
      `\nAcknowledgements already given are kept as a record, not deleted.`);
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(`/policies/${policyId}/reapply-audience`);
      window.alert(`Done. Removed from ${r.data.removed} person(s)` +
        (r.data.removed_acknowledged ? ` (${r.data.removed_acknowledged} had already acknowledged)` : '') +
        `, added for ${r.data.added}. It now applies to ${r.data.audience}.`);
      loadAudience(); onSaved();
    } catch (e) { window.alert(e.response?.data?.error || 'Could not apply it'); }
    finally { setBusy(false); }
  };

  const toggle = (type, id) => setSel(s => ({
    ...s, [type]: s[type].includes(id) ? s[type].filter(x => x !== id) : [...s[type], id] }));

  const save = async () => {
    setBusy(true);
    const payload = [];
    ['campus','department','role'].forEach(t => sel[t].forEach(id => payload.push({ rule_type:t, ref_id:id })));
    try { await api.put(`/policies/${policyId}/applicability`, { rules: payload }); onSaved(); loadAudience(); }
    catch (e) {} finally { setBusy(false); }
  };

  const Group = ({ title, type, items, label }) => (
    <div className="card" style={{ marginBottom:12 }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>{title}</span>
        {canManage && <div className="flex-gap" style={{ gap:6 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setSel(s => ({ ...s, [type]: items.map(i => i.id) }))}>All</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSel(s => ({ ...s, [type]: [] }))}>None</button>
        </div>}
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, maxHeight:150, overflowY:'auto' }}>
        {items.map(i => (
          <label className="opt" key={i.id} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, cursor: canManage ? 'pointer' : 'default' }}>
            <input type="checkbox" disabled={!canManage} checked={sel[type].includes(i.id)} onChange={() => toggle(type, i.id)} />
            {label(i)}
          </label>
        ))}
        {!items.length && <span style={{ fontSize:12, color:'var(--text2)' }}>None configured.</span>}
      </div>
      <div style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>
        Nothing ticked = no restriction on this dimension.
      </div>
    </div>
  );

  return (
    <div>
      <div className="card" style={{ marginBottom:12, borderLeft:'3px solid var(--accent)' }}>
        <div style={{ fontSize:13 }}>
          {audience
            ? audience.has_rules
              ? <>This policy applies to <b style={{ color:'var(--accent)', fontSize:16 }}>{audience.count}</b> staff member(s).</>
              : <>No rules set — this policy currently applies to <b>everyone</b> ({audience.count} staff).</>
            : 'Working out the audience…'}
        </div>
        {/* The published version keeps the audience it was published to. If the
            two have drifted apart, say so plainly and offer to fix it. */}
        {drift && drift.published && (drift.remove.length > 0 || drift.add.length > 0) && (
          <div style={{ background:'#FDF0CD', border:'1px solid #E8CE86', borderRadius:8,
            padding:'10px 12px', margin:'10px 0', fontSize:12.5, color:'#7A5300' }}>
            <b>The published version does not match this applicability yet.</b>
            <div style={{ marginTop:5 }}>
              {drift.remove.length > 0 && <div>
                {drift.remove.length} person(s) still hold it who are no longer covered
                {drift.remove.slice(0, 6).length > 0 &&
                  <span style={{ opacity:.85 }}> — {drift.remove.slice(0, 6).map(u => u.full_name).join(', ')}
                    {drift.remove.length > 6 ? ` and ${drift.remove.length - 6} more` : ''}</span>}
              </div>}
              {drift.add.length > 0 && <div>{drift.add.length} newly covered person(s) have not been asked yet</div>}
            </div>
            {canManage && (
              <button className="btn btn-primary btn-sm" style={{ marginTop:8 }} disabled={busy} onClick={reapply}>
                {busy ? 'Applying…' : 'Apply to the published version'}
              </button>
            )}
          </div>
        )}
        <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
          Applicability can be changed at any time. Adding a department later brings those staff into scope for the published version.
        </div>
      </div>

      <Group title="CAMPUSES" type="campus" items={campuses} label={c => c.name} />
      <Group title="DEPARTMENTS" type="department" items={depts} label={d => d.name} />
      <Group title="EMPLOYEE GROUPS (roles)" type="role" items={roles} label={r => r.name} />

      {canManage && <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save applicability'}</button>}
    </div>
  );
}

function Activity({ id }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/policies/${id}/audit`).then(r => setRows(r.data)).catch(()=>{}); }, [id]);
  const when = t => t ? new Date(t).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '';
  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', padding:'10px 12px' }}>AUDIT TRAIL <span style={{ fontWeight:400 }}>— append-only</span></div>
      {!rows.length ? <div className="empty-state"><p>No activity recorded yet.</p></div> : (
        <div style={{ padding:'0 14px 12px' }}>
          {rows.map(a => (
            <div key={a.id} className="flex-gap" style={{ gap:10, alignItems:'center', padding:'7px 0', borderTop:'1px solid var(--border)' }}>
              <span className="badge badge-normal" style={{ fontSize:10 }}>{a.action.replace(/_/g,' ')}</span>
              <span style={{ flex:1, fontSize:12.5 }}>{a.user_name || 'system'}{a.version_no ? ` · v${a.version_no}` : ''}</span>
              <span style={{ fontSize:11, color:'var(--text2)' }}>{when(a.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
